import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";

const LanguageContext = createContext();

function deepMerge(base, override) {
  if (!override) return base ?? {};
  if (!base) return override ?? {};

  const result = { ...base };
  for (const [key, value] of Object.entries(override)) {
    if (
      value &&
      typeof value === "object" &&
      !Array.isArray(value) &&
      base[key] &&
      typeof base[key] === "object" &&
      !Array.isArray(base[key])
    ) {
      result[key] = deepMerge(base[key], value);
    } else {
      result[key] = value;
    }
  }
  return result;
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const stored = localStorage.getItem("selectedLanguage");
    if (stored === "es") {
      localStorage.setItem("selectedLanguage", "fr");
      return "fr";
    }
    return stored || "fr";
  });
  const [translations, setTranslations] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Memoized function to load translations
  const loadTranslations = useCallback(async (lang) => {
    try {
      setIsLoading(true);
      const enModule = await import("../locales/en/common.json");
      const enTranslations = enModule.default || enModule;

      if (lang === "en") {
        setTranslations(enTranslations);
      } else {
        const translationModule = await import(`../locales/${lang}/common.json`);
        const localeTranslations = translationModule.default || translationModule;
        setTranslations(deepMerge(enTranslations, localeTranslations));
      }
      localStorage.setItem("selectedLanguage", lang);
    } catch (error) {
      console.error(`Failed to load ${lang} translations:`, error);
      const fallbacks = lang === "fr" ? ["en"] : ["fr", "en"];
      let loaded = false;
      for (const fb of fallbacks) {
        try {
          const fallbackModule = await import(`../locales/${fb}/common.json`);
          setTranslations(fallbackModule.default || fallbackModule);
          loaded = true;
          break;
        } catch (fallbackError) {
          console.error(`Failed to load ${fb} fallback translations:`, fallbackError);
        }
      }
      if (!loaded) {
        setTranslations({});
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load translations when language changes
  useEffect(() => {
    if (language) {
      loadTranslations(language);
    }
  }, [language, loadTranslations]);

  // Stable setLanguage function
  const setLanguageStable = useCallback(
    (lang) => {
      if (lang !== language) {
        setLanguage(lang);
      }
    },
    [language]
  );

  const value = {
    language,
    setLanguage: setLanguageStable,
    translations,
    isLoading,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

LanguageProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
};
